/**
 * EntryFeeSection component - displays entry fee information in TournamentDetails.
 *
 * Task 15.1: Conditional FREE/PAID views
 *
 * Renders:
 * - FREE tournaments: "Free Entry" + "No Prize Pool"
 * - PAID tournaments: Entry fee amount + Polygon tooltip trigger
 */

import React from 'react';
import styled from 'styled-components';
import { EntryFee } from '../../../types/tournament';
import {
  formatAmountPOL,
  formatAmountUSD,
  calculateEstimatedPrizePool,
  formatPrizePool,
} from './entryFeeHelpers';

export interface EntryFeeSectionProps {
  entryFee?: EntryFee;
  usersLimit?: number;
  polPrice?: number | null;
}

// Styled components for Entry Fee section
const EntryFeeContainer = styled.div`
  margin: 20px 0;
  padding: 16px;
  border-radius: 10px;
  text-align: center;
`;

const FreeEntryContainer = styled(EntryFeeContainer)`
  background-color: #1e2227;
  border: 1px solid #4a4a4a;
`;

const PaidEntryContainer = styled(EntryFeeContainer)`
  background: linear-gradient(135deg, #1e2227 0%, #15191e 100%);
  border: 1px solid #52d381;
`;

const EntryFeeLabel = styled.div`
  color: #9ca3af;
  font-size: 14px;
  margin-bottom: 8px;
`;

const FreeLabel = styled.span`
  color: #efefef;
  font-size: 16px;
  font-weight: 400;
`;

const NoPrizePoolLabel = styled.div`
  color: #9ca3af;
  font-size: 14px;
  margin-top: 8px;
`;

const PaidAmount = styled.span`
  color: #52d381;
  font-size: 18px;
  font-weight: 600;
`;

const EntryFeeAmountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const TooltipTrigger = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 24px;
  height: 24px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: 2px solid #52d381;
    outline-offset: 2px;
  }
`;

const UsdEquivalent = styled.span`
  color: #9ca3af;
  font-size: 14px;
  font-weight: 400;
  margin-left: 4px;
`;

const PrizePoolRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(82, 211, 129, 0.2);
`;

const PrizePoolLabel = styled.span`
  color: #9ca3af;
  font-size: 14px;
`;

const PrizePoolAmount = styled.span`
  color: #52d381;
  font-size: 16px;
  font-weight: 600;
  margin-left: 8px;
`;

/**
 * Determines if the tournament is FREE based on entryFee.
 */
const isFreeEntry = (entryFee?: EntryFee): boolean => {
  if (!entryFee) return true;
  if (entryFee.type === 'FREE') return true;
  if (entryFee.amount === '0' || parseFloat(entryFee.amount) === 0) return true;
  return false;
};

/**
 * FreeEntryView - Renders the FREE tournament entry fee section.
 */
const FreeEntryView: React.FC = () => (
  <FreeEntryContainer>
    <FreeLabel>Free Entry</FreeLabel>
    <NoPrizePoolLabel>No Prize Pool</NoPrizePoolLabel>
  </FreeEntryContainer>
);

interface PaidEntryViewProps {
  entryFee: EntryFee;
  usersLimit?: number;
  polPrice?: number | null;
}

/**
 * PaidEntryView - Renders the PAID tournament entry fee section.
 * Shows entry fee in POL, optional USD equivalent, and estimated prize pool.
 */
const PaidEntryView: React.FC<PaidEntryViewProps> = ({
  entryFee,
  usersLimit,
  polPrice,
}) => {
  const formattedPOL = formatAmountPOL(entryFee.amount);
  const formattedUSD = formatAmountUSD(entryFee.amount, polPrice ?? null);
  const prizePool = calculateEstimatedPrizePool(entryFee, usersLimit);

  return (
    <PaidEntryContainer>
      <EntryFeeLabel>Entry Fee</EntryFeeLabel>
      <EntryFeeAmountRow>
        <PaidAmount>{formattedPOL}</PaidAmount>
        {formattedUSD && <UsdEquivalent>({formattedUSD})</UsdEquivalent>}
        <TooltipTrigger
          aria-label="About Polygon network"
          type="button"
        >
          ℹ️
        </TooltipTrigger>
      </EntryFeeAmountRow>

      {prizePool != null && (
        <PrizePoolRow>
          <PrizePoolLabel>Est. Prize Pool:</PrizePoolLabel>
          <PrizePoolAmount>{formatPrizePool(prizePool)}</PrizePoolAmount>
        </PrizePoolRow>
      )}
    </PaidEntryContainer>
  );
};

/**
 * EntryFeeSection - Router component that decides FREE vs PAID rendering.
 */
export const EntryFeeSection: React.FC<EntryFeeSectionProps> = ({
  entryFee,
  usersLimit,
  polPrice,
}) => {
  // Log warning for missing entryFee (monitoring/debugging)
  if (!entryFee) {
    console.warn('[EntryFeeSection] Missing entryFee, defaulting to FREE');
  }

  // Determine if FREE or PAID
  if (isFreeEntry(entryFee)) {
    return <FreeEntryView />;
  }

  return <PaidEntryView entryFee={entryFee!} usersLimit={usersLimit} polPrice={polPrice} />;
};

export default EntryFeeSection;
